import { useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { CustomButton } from '../../components/common/CustomButton'
import { impersonateShopOwner, type ImpersonationLink } from '../../services/adminApi'

interface ImpersonateDialogProps {
  shopId: string
  shopName: string
  ownerEmail: string
  mode?: 'web' | 'mobile'
  onClose: () => void
}

const MOBILE_SCHEME = 'barta://impersonate'

function buildMobileDeeplink(link: ImpersonationLink): string {
  const params = new URLSearchParams({
    email: link.owner_email,
    otp: link.otp,
    shop_id: link.shop_id,
    shop_name: link.shop_name,
  })
  return `${MOBILE_SCHEME}?${params.toString()}`
}

export function ImpersonateDialog({
  shopId,
  shopName,
  ownerEmail,
  mode = 'web',
  onClose,
}: ImpersonateDialogProps) {
  const [loading, setLoading] = useState(false)
  const [link, setLink] = useState<ImpersonationLink | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const handleGenerate = async () => {
    setLoading(true)
    setError(null)
    const result = await impersonateShopOwner(shopId)
    setLoading(false)
    if (result.ok && result.data) {
      setLink(result.data)
    } else {
      setError(result.error?.message || 'Failed to generate link')
    }
  }

  const handleOpen = () => {
    if (link) window.open(link.action_link, '_blank')
  }

  const mobileLink = link ? buildMobileDeeplink(link) : null

  const handleCopyMobile = async () => {
    if (!mobileLink) return
    await navigator.clipboard.writeText(mobileLink)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const title = mode === 'mobile' ? 'Open in Mobile App' : 'Manage as Owner'

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-2xl bg-white dark:bg-[#1a1520] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-heading text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
          {title}
        </h3>

        {!link ? (
          <>
            <div className="mb-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
              <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">Security Notice</p>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                {mode === 'mobile' ? (
                  <>
                    This generates a one-time mobile sign-in for <strong>{ownerEmail}</strong>{' '}
                    (shop: {shopName}). Scan the QR with the Barta mobile app to impersonate. This
                    action is audit-logged.
                  </>
                ) : (
                  <>
                    This generates a one-time login link for <strong>{ownerEmail}</strong> (shop:{' '}
                    {shopName}). Open it in an incognito window to keep your admin session. This
                    action is audit-logged.
                  </>
                )}
              </p>
            </div>
            {error && <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>}
            <div className="flex gap-2">
              <CustomButton onClick={handleGenerate} loading={loading} variant="primary">
                Generate Link
              </CustomButton>
              <CustomButton onClick={onClose} variant="secondary">
                Cancel
              </CustomButton>
            </div>
          </>
        ) : mode === 'mobile' && mobileLink ? (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              Scan with the Barta mobile app. Single-use, expires in{' '}
              {Math.round(link.expires_in / 60)} minutes.
            </p>
            <div className="flex justify-center mb-4 rounded-xl bg-white p-4">
              <QRCodeSVG value={mobileLink} size={220} level="M" includeMargin={false} />
            </div>
            <div className="mb-4 break-all rounded-lg bg-gray-100 dark:bg-gray-800 px-3 py-2 font-mono text-xs text-gray-700 dark:text-gray-200">
              {mobileLink}
            </div>
            <div className="flex gap-2">
              <CustomButton onClick={handleCopyMobile} variant="primary">
                {copied ? 'Copied!' : 'Copy Link'}
              </CustomButton>
              <CustomButton onClick={onClose} variant="secondary">
                Close
              </CustomButton>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Link generated. It works once and expires in{' '}
              {Math.round(link.expires_in / 60)} minutes. Use an incognito window.
            </p>
            <div className="flex gap-2">
              <CustomButton onClick={handleOpen} variant="primary">
                Open in New Window
              </CustomButton>
              <CustomButton onClick={onClose} variant="secondary">
                Close
              </CustomButton>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
